import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RootState, useAppSelector } from '../store';
import { openApiService } from '../Services/openApiService';
import { OpenApiConfig as OpenApiConfigType } from '../Types';
import toast from 'react-hot-toast';

export interface OpenApiConfigFormData {
    title: string;
    authType: 'none' | 'apiKey' | 'bearer' | 'basic';
    authData: {
        // For apiKey
        apiKeyLocation?: 'header' | 'query';
        apiKeyName?: string;
        apiKeyValue?: string;
        // For bearer
        token?: string;
        // For basic
        username?: string;
        password?: string;
    };
    openApiSpec: string;
}

type UseOpenApiConfigOptions = {
    onSaved?: () => void;
};

const useOpenApiConfig = ({ onSaved }: UseOpenApiConfigOptions = {}) => {
    const { configIdentifier } = useParams();
    const navigate = useNavigate();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const isNew = configIdentifier === 'new';
    const [activeTab, setActiveTab] = useState(0);
    const isMobile = window.innerWidth < 768;
    const boxHeightClassesStr = isMobile ? 'h-[calc(var(--app-height)-70px)]' : 'h-[calc(var(--app-height)-140px)]';

    const [formData, setFormData] = useState<OpenApiConfigFormData>({
        title: '',
        authType: 'none',
        authData: {},
        openApiSpec: '',
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [config, setConfig] = useState<OpenApiConfigType | null>(null);

    // Load existing config if editing
    const loadConfig = useCallback(async () => {
        setIsLoading(true);
        if (!isNew && configIdentifier) {
            try {
                const response = await openApiService.getConfig(configIdentifier);
                const configData = response.data;
                setConfig(configData);

                // Normalize auth data field names
                const normalizedAuthData = {
                    apiKeyLocation: configData.authData?.apiKeyLocation || 'header',
                    apiKeyName: configData.authData?.apiKeyName || '',
                    apiKeyValue: configData.authData?.apiKeyValue || '',
                    token: configData.authData?.token || '',
                    username: configData.authData?.username || '',
                    password: configData.authData?.password || '',
                };

                setFormData({
                    title: configData.title,
                    authType: configData.authType as 'none' | 'apiKey' | 'bearer' | 'basic',
                    authData: normalizedAuthData,
                    openApiSpec: configData.openApiSpec || '',
                });
            } catch (error) {
                console.error('Error loading config:', error);
                toast.error(i18n.translate({ id: 'load.error', sourceName: 'OpenAPI' }));
            } finally {
                setIsLoading(false);
            }
        }
        setIsLoading(false);
    }, [configIdentifier, isNew, i18n]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = i18n.translate({ id: 'validation.title.required', sourceName: 'OpenAPI' });
        }

        if (!formData.openApiSpec.trim()) {
            newErrors.openApiSpec = i18n.translate({ id: 'validation.spec.required', sourceName: 'OpenAPI' });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async (e?: React.FormEvent) => {
        const loadingToastId = toast.loading(i18n.translate({ id: 'form.saving', sourceName: 'OpenAPI' }));
        if (e) {
            e.preventDefault();
        }

        if (!validateForm()) {
            toast.dismiss(loadingToastId);
            return;
        }

        try {
            const normalizedFormData = {
                ...formData,
                authData: {
                    apiKeyLocation: formData.authData.apiKeyLocation,
                    apiKeyName: formData.authData.apiKeyName,
                    apiKeyValue: formData.authData.apiKeyValue,
                    token: formData.authData.token,
                    username: formData.authData.username,
                    password: formData.authData.password,
                },
            };
            if (isNew) {
                const response = await openApiService.createConfig(normalizedFormData);
                toast.dismiss(loadingToastId);

                if (response.data.success === false) {
                    toast.error(response.data.error || i18n.translate({ id: 'save.error', sourceName: 'OpenAPI' }));
                    return;
                }
                onSaved?.();
                toast.success(i18n.translate({ id: 'create.success', sourceName: 'OpenAPI' }));
                navigate(`/admin/openapiconfig/${response.data.identifier}`);
            } else if (configIdentifier) {
                const response = await openApiService.updateConfig(configIdentifier, normalizedFormData);
                toast.dismiss(loadingToastId);

                if (response.data.success === false) {
                    toast.error(response.data.error || i18n.translate({ id: 'save.error', sourceName: 'OpenAPI' }));
                    return;
                }
                toast.success(i18n.translate({ id: 'update.success', sourceName: 'OpenAPI' }));
                onSaved?.();
                loadConfig();
            }
        } catch (error: any) {
            console.error('Error saving config:', error);
            if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error(i18n.translate({ id: 'save.error', sourceName: 'OpenAPI' }));
            }
        } finally {
        }
    };

    const handleCancel = () => {
        navigate('/admin/assistantfunctions?tab=openapi');
    };

    const updateFormData = (updates: Partial<OpenApiConfigFormData>) => {
        setFormData((prev) => ({ ...prev, ...updates }));
    };

    const updateAuthData = (authUpdates: Record<string, any>) => {
        setFormData((prev) => ({
            ...prev,
            authData: { ...prev.authData, ...authUpdates },
        }));
    };

    return {
        formData,
        errors,
        isLoading,
        config,
        isNew,
        handleSave,
        handleCancel,
        updateFormData,
        updateAuthData,
        setFormData,
        activeTab,
        setActiveTab,
        isMobile,
        boxHeightClassesStr,
        configIdentifier,
    };
};

export default useOpenApiConfig;
